import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
  defaultLadderBands,
  persistedLadderBands,
  ladderSummary,
  normalizeDecimalInput,
} from '../../utils/sniperArm';
import {
  bulkBlockedReason,
  parseBulkPatch,
  type BulkLadderOverlay,
  type BulkSelectionMarket,
  type SniperBulkPatch,
} from '../../utils/sniperBulk';
import { SniperConfigPopover } from './SniperConfigPopover';
import { SniperTriState } from './SniperTriState';

export type { SniperBulkPatch };

/**
 * Bulk-edit bar for the markets SELECTED within one event card. The operator
 * types only the params they want to change (blank = leave untouched) and hits
 * Apply to write them to ALL selected markets in one request. Enable/Disable act
 * on the whole selection too. Add a new bulk param here + in `SniperBulkPatch`
 * and the wiring below — the shape is intentionally extensible.
 *
 * Validation mirrors `SniperMarketRow` and the backend DTO: size and the auto-NO
 * leg size (`noSize`) >= 1, the auto-NO ceiling ∈ (0,1], gate 1..100 (→ 0..1).
 * The five switches are tri-state (— = untouched, On/Off = set). A field left
 * blank/untouched is simply omitted from the patch; Apply is enabled as long as
 * at least one typed field is valid AND none typed is invalid. `noSize` blank
 * always means "untouched" here — unlike the per-row field, bulk has no "clear
 * to inherit `size`" action.
 */
export function SniperBulkBar({
  count,
  busy,
  selection = [],
  onApplyParams,
  onSetEnabled,
  onClear,
}: {
  count: number;
  busy: boolean;
  /**
   * The selected markets as they are stored TODAY. The server merges each row
   * and re-validates the whole config, so one market can 400 the entire bulk:
   * this is what the floor bounds (range + required) and the ladder seeding are
   * resolved from.
   */
  selection?: BulkSelectionMarket[];
  /** Receives an `onApplied` callback — invoked ONLY once the write succeeds. */
  onApplyParams: (patch: SniperBulkPatch, onApplied: () => void) => void;
  onSetEnabled: (enabled: boolean) => void;
  onClear: () => void;
}) {
  const [size, setSize] = useState('');
  const [gate, setGate] = useState('');
  // Auto-NO period-close tail: a price + a size + two tri-state switches.
  const [noMaxPrice, setNoMaxPrice] = useState('');
  const [noSize, setNoSize] = useState('');
  const [autoNoEnabled, setAutoNoEnabled] = useState('');
  const [autoNoIncludeExtraTime, setAutoNoIncludeExtraTime] = useState('');
  // The sell switches: '' = leave untouched, 'true'/'false' = set.
  const [sellOnVar, setSellOnVar] = useState('');
  const [sellOnAnnulledGoal, setSellOnAnnulledGoal] = useState('');
  const [varRebuyEnabled, setVarRebuyEnabled] = useState('');
  // The ladder has no "blank string" state, so untouched is an explicit null.
  const [ladder, setLadder] = useState<BulkLadderOverlay | null>(null);

  /**
   * The rungs the popover opens on. `ladderBands` is REPLACED wholesale, and
   * this editor has no per-selection value to show, so seeding it from the
   * shipped defaults would make a one-rung table appear over markets carrying
   * three hand-tuned rungs — and any edit would then truncate all of them. When
   * every selected market agrees, seed from THEM; when they differ there is no
   * honest seed, so fall back to the defaults and warn loudly instead.
   */
  const sharedBands = useMemo(() => {
    if (selection.length === 0) return null;
    const first = persistedLadderBands(selection[0].ladderBands);
    const key = JSON.stringify(first);
    return selection.every(
      (m) => JSON.stringify(persistedLadderBands(m.ladderBands)) === key,
    )
      ? first
      : null;
  }, [selection]);

  const draft = useMemo(
    () => ({
      size,
      gate,
      noMaxPrice,
      noSize,
      sellOnVar,
      sellOnAnnulledGoal,
      varRebuyEnabled,
      autoNoEnabled,
      autoNoIncludeExtraTime,
      ladder,
      selection,
    }),
    [
      size,
      gate,
      noMaxPrice,
      noSize,
      sellOnVar,
      sellOnAnnulledGoal,
      varRebuyEnabled,
      autoNoEnabled,
      autoNoIncludeExtraTime,
      ladder,
      selection,
    ],
  );

  const parsed = useMemo(() => parseBulkPatch(draft), [draft]);

  // Enable bypasses `parseBulkPatch` entirely — it writes only `{enabled:true}` —
  // but the server runs its FULL validation when a market is enabled, so a
  // migrated row with no rungs (or a legacy one that sells with no floor) rejects
  // the whole selection and arms nothing. Same check, same wording.
  const enableBlockedReason = useMemo(
    () => bulkBlockedReason({ enabled: true }, selection),
    [selection],
  );

  // Enable/Disable writes ONLY `{enabled}`. With params typed but not applied,
  // that would arm markets at their OLD size/gate while the bar still shows the
  // new values as if they had been written — fail closed and make the operator
  // Apply or clear first.
  const hasDrafts =
    size !== '' ||
    gate !== '' ||
    noMaxPrice !== '' ||
    noSize !== '' ||
    sellOnVar !== '' ||
    sellOnAnnulledGoal !== '' ||
    varRebuyEnabled !== '' ||
    autoNoEnabled !== '' ||
    autoNoIncludeExtraTime !== '' ||
    ladder !== null;

  const clearDrafts = () => {
    setSize('');
    setGate('');
    setNoMaxPrice('');
    setNoSize('');
    setSellOnVar('');
    setSellOnAnnulledGoal('');
    setVarRebuyEnabled('');
    setAutoNoEnabled('');
    setAutoNoIncludeExtraTime('');
    setLadder(null);
  };

  const apply = () => {
    if (!parsed.canApply || busy) return;
    // Drafts clear ONLY once the write lands. Clearing optimistically would drop
    // `hasDrafts` on a FAILED apply, re-opening Enable over params that were
    // never written — the exact footgun the gate exists to close.
    onApplyParams(parsed.patch, clearDrafts);
  };

  const fieldSx = {
    flexShrink: 0,
    '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
    '& .MuiInputLabel-root': { fontSize: 10.5 },
    '& .MuiInputLabel-shrink': { fontSize: 11 },
  };
  const selectSx = { ...fieldSx, width: 92 };
  const btnSx = { flexShrink: 0, minWidth: 0, fontSize: 12 };

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1,
        py: 0.5,
        borderColor: 'primary.main',
        bgcolor: 'rgba(33,150,243,0.06)',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="nowrap"
        useFlexGap
        sx={{ overflowX: 'auto' }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}
        >
          {count} selected
        </Typography>
        <Box sx={{ width: '1px', flexShrink: 0, height: 20, bgcolor: 'divider', mx: 0.25 }} />

        <TextField
          size="small"
          label="Size"
          value={size}
          onChange={(e) => setSize(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'bulk size' }}
          sx={{ width: 66, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Gate %"
          value={gate}
          onChange={(e) => setGate(e.target.value.replace(/[^0-9]/g, ''))}
          inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': 'bulk gate percent' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 11 } }}>
                %
              </InputAdornment>
            ),
          }}
          sx={{ width: 80, ...fieldSx }}
        />
        <Tooltip title="Auto-NO period-close tail — worst per-contract price the NO buy may pay">
          <TextField
            size="small"
            label="No $"
            value={noMaxPrice}
            onChange={(e) => setNoMaxPrice(normalizeDecimalInput(e.target.value))}
            inputProps={{
              inputMode: 'decimal',
              'aria-label': 'bulk auto-no max price',
            }}
            sx={{ width: 66, ...fieldSx }}
          />
        </Tooltip>
        <Tooltip title="Auto-NO sweep size (contracts) — independent of the main Size. Blank leaves it untouched">
          <TextField
            size="small"
            label="No Size"
            value={noSize}
            onChange={(e) => setNoSize(normalizeDecimalInput(e.target.value))}
            inputProps={{
              inputMode: 'decimal',
              'aria-label': 'bulk auto-no size',
            }}
            sx={{ width: 66, ...fieldSx }}
          />
        </Tooltip>
        <SniperTriState
          label="Sell VAR"
          tooltip="Sell on VAR — post the exit the moment a VAR review follows our goal"
          ariaLabel="bulk sell on var"
          value={sellOnVar}
          onChange={setSellOnVar}
          sx={selectSx}
        />
        <SniperTriState
          label="Sell annul"
          tooltip="Sell on annulled goal — dump to the floor once the goal is annulled"
          ariaLabel="bulk sell on annulled goal"
          value={sellOnAnnulledGoal}
          onChange={setSellOnAnnulledGoal}
          sx={selectSx}
        />
        <SniperTriState
          label="Rebuy"
          tooltip="All-clear rebuy if the VAR confirms the goal"
          ariaLabel="bulk var rebuy enabled"
          value={varRebuyEnabled}
          onChange={setVarRebuyEnabled}
          sx={selectSx}
        />
        <SniperTriState
          label="Auto-NO"
          tooltip="Auto-NO period-close tail — buy the NO the instant the period ends without the Over crossing"
          ariaLabel="bulk auto-no enabled"
          value={autoNoEnabled}
          onChange={setAutoNoEnabled}
          sx={selectSx}
        />
        <SniperTriState
          label="ET"
          tooltip="Include extra time in the auto-NO tail"
          ariaLabel="bulk auto-no include extra time"
          value={autoNoIncludeExtraTime}
          onChange={setAutoNoIncludeExtraTime}
          sx={selectSx}
        />
        <SniperConfigPopover
          value={{
            ladderBands: ladder?.ladderBands ?? sharedBands ?? defaultLadderBands(),
            floorPrice: ladder?.floorPrice ?? null,
            varPreventiveExitOffset:
              ladder?.varPreventiveExitOffset ?? DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
          }}
          onApply={(next, touched) =>
            setLadder((prev) => {
              // OR-MERGE with what is already staged: a second session diffs
              // against the seed it opened on, so writing `touched` wholesale
              // would drop the first session's edit — the bar would still
              // advertise the new rungs while the patch carried only the floor.
              const merged = {
                ladderBands: !!prev?.touched.ladderBands || touched.ladderBands,
                floorPrice: !!prev?.touched.floorPrice || touched.floorPrice,
                varPreventiveExitOffset:
                  !!prev?.touched.varPreventiveExitOffset ||
                  touched.varPreventiveExitOffset,
              };
              // Nothing edited across either session = nothing staged, so the bar
              // does not claim a draft (and Enable/Disable stay open) for a
              // popover the operator effectively cancelled out of.
              return merged.ladderBands ||
                merged.floorPrice ||
                merged.varPreventiveExitOffset
                ? {
                    ladderBands: next.ladderBands,
                    floorPrice: next.floorPrice,
                    // A cleared cell means "use the engine default", which is
                    // this exact number — the patch DTO has no way to say unset.
                    varPreventiveExitOffset:
                      next.varPreventiveExitOffset ??
                      DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
                    touched: merged,
                  }
                : null;
            })
          }
          // Each selected market keeps its own floor, so one is never demanded
          // here — and a blank floor is expressible (it just means "leave every
          // market's own alone"). Mirrors `parseBulkPatch`.
          floorRequired={false}
          floorClearable
          disabled={busy}
          renderTrigger={(open) => (
            <Tooltip title="Ladder config — untouched unless you Apply inside.">
              <Button
                size="small"
                variant={ladder ? 'contained' : 'outlined'}
                onClick={open}
                disabled={busy}
                // The label becomes the staged summary, so it needs a stable
                // handle of its own to stay findable once something is staged.
                aria-label="bulk ladder config"
                sx={{ ...btnSx, px: 1, whiteSpace: 'nowrap' }}
              >
                {ladder ? ladderSummary(ladder.ladderBands, ladder.floorPrice) : 'Ladder —'}
              </Button>
            </Tooltip>
          )}
        />
        {/* The selected markets do NOT share one ladder, so there was no honest
            value to seed the table with — say so before the operator applies a
            replacement over rungs they never saw. */}
        {ladder?.touched.ladderBands && sharedBands == null && selection.length > 0 && (
          <Tooltip title="The selected markets carry different ladders, so this table could not show theirs. Applying REPLACES the rungs on all of them.">
            <Chip
              size="small"
              color="warning"
              icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
              label={`replaces the rungs on all ${selection.length}`}
              sx={{ flexShrink: 0, height: 22, fontSize: 11, fontWeight: 700 }}
            />
          </Tooltip>
        )}
        {/* Staged ladder edits are cleared with the rest of the drafts on a
            successful Apply; this X is the only way to un-stage one alone. */}
        {ladder && (
          <Tooltip title="Leave the ladder untouched">
            <IconButton
              size="small"
              onClick={() => setLadder(null)}
              disabled={busy}
              aria-label="clear bulk ladder"
              sx={{ p: 0.25, flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={parsed.reason ?? parsed.note ?? ''}>
          <span>
            <Button
              size="small"
              variant="contained"
              onClick={apply}
              disabled={!parsed.canApply || busy}
              sx={{ ...btnSx, px: 1.25 }}
            >
              Apply
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ width: '1px', flexShrink: 0, height: 20, bgcolor: 'divider', mx: 0.25 }} />
        <Tooltip
          title={
            hasDrafts
              ? 'Apply or clear the typed params first — Enable writes only the on/off flag'
              : (enableBlockedReason ?? 'Enable all selected markets')
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onSetEnabled(true)}
              disabled={busy || hasDrafts || enableBlockedReason !== null}
              sx={{ ...btnSx, px: 1 }}
            >
              Enable
            </Button>
          </span>
        </Tooltip>
        {/* NEVER gated on typed drafts, unlike Enable: turning markets OFF
            cannot make one fire, and the row states the same principle —
            gating the safe direction puts friction on the one action an
            operator may need in a hurry, mid-match. */}
        <Tooltip title="Disable all selected markets">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onSetEnabled(false)}
              disabled={busy}
              sx={{ ...btnSx, px: 1 }}
            >
              Disable
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <Tooltip title="Clear selection">
          <span>
            <IconButton size="small" onClick={onClear} disabled={busy} aria-label="clear selection" sx={{ p: 0.5, flexShrink: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
